import { useState } from "react";
import { useLocation } from "wouter";
import { Loader2, Send } from "lucide-react";
import { SheetModal } from "@/components/SheetModal";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { useToast } from "@/components/ux/Toast";
import { useCreateClubPost } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { AddPhotoButton, AttachmentStrip, LengthCounter } from "./ClubComposer";
import { composeBody, MAX_POST_LENGTH, usePostAttachments } from "./usePostAttachments";

const MAX_TITLE_LENGTH = 150; // ClubAPI.CreatePostRequestSchema

interface Props {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  clubName: string;
}

/**
 * Sheet for starting a new forum thread: title, rich text body and photos.
 * Stays mounted while closed, so closing keeps the draft; posting clears it
 * and opens the new thread.
 */
export function ClubNewPostSheet({ isOpen, onClose, slug, clubName }: Props) {
  const { t } = useT();
  const toast = useToast();
  const [, navigate] = useLocation();
  const createPost = useCreateClubPost(slug);
  const attachments = usePostAttachments();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");

  const body = composeBody(text, attachments.images);
  const canPost =
    !!title.trim() && !!body && body.length <= MAX_POST_LENGTH && !createPost.isPending && !attachments.uploading;

  const submit = () => {
    if (!canPost) return;
    createPost.mutate(
      { title: title.trim(), body },
      {
        onSuccess: (post) => {
          setTitle("");
          setText("");
          attachments.reset();
          onClose();
          navigate(`/clubs/${slug}/posts/${post.id}`);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-2xl">
      <div className="px-5 pt-5 pb-4">
        <h2 className="text-lg font-semibold">{t("clubs.newPost")}</h2>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{clubName}</p>
      </div>

      <div className="px-5 pb-2 space-y-4">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={MAX_TITLE_LENGTH}
          placeholder={t("clubs.titlePlaceholder")}
          className="input w-full font-semibold"
        />
        <div className="rounded-xl border border-black/10 dark:border-white/10 px-3.5 py-3">
          <MarkdownEditor value={text} onChange={setText} onSubmit={submit} placeholder={t("clubs.composerPlaceholder")} />
        </div>
        <AttachmentStrip attachments={attachments} />
        <div className="flex items-center gap-2">
          <AddPhotoButton attachments={attachments} />
          <span className="ml-auto">
            <LengthCounter length={body.length} />
          </span>
        </div>
      </div>

      <div className="px-5 py-4 flex items-center gap-2 sticky bottom-0 bg-white dark:bg-neutral-900">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t("common.buttons.cancel")}
        </button>
        <button
          type="button"
          disabled={!canPost}
          onClick={submit}
          className="btn btn-primary flex-1 flex items-center justify-center gap-2"
        >
          {createPost.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t("clubs.post")}
        </button>
      </div>
    </SheetModal>
  );
}
